<template>
  <!--
    Sessions.vue

    Tracks and Side Track Registration management view.
    Features:
      - Sessions list with basic information
      - Session details dialog with registered users
      - Search and filtering capabilities
      - Uses Vuetify data table and dialogs
  -->
  <v-container>
    <!-- Session details dialog -->
    <session-details-dialog
      :dialog="sessionDetailsDialog.visible"
      :session="sessionDetailsDialog.session"
      @close="closeSessionDetailsDialog"
    />

    <!-- Session counts display dialog -->
    <session-counts-display-dialog
      :dialog="sessionCountsDialog.visible"
      :session="sessionCountsDialog.session"
      @close="closeSessionCountsDialog"
    />

    <v-card>
      <div class="pa-5 d-flex align-center">
        <v-spacer></v-spacer>

        <!-- Search field -->
        <v-text-field
          v-model="tableSearch"
          append-icon="mdi-magnify"
          label="Search sessions"
          clearable
          single-line
          hide-details
        ></v-text-field>

        <!-- Refresh button -->
        <v-btn class="ml-2" color="primary" @click="getSessionsList()">
          <v-icon>mdi-reload</v-icon>
        </v-btn>
      </div>

      <!-- Sessions data table -->
      <v-data-table
        :loading="sessionsLoading"
        :headers="tableHeaders"
        :items="filteredSessions"
        :items-per-page="25"
        loading-text="Loading sessions... Please wait"
        item-key="id"
        class="elevation-1"
      >
        <!-- Date start column with readable date format -->
        <template v-slot:item.dateStart="{ item }">
          {{ item.dateStart | readableDateTime }}
        </template>

        <!-- Date end column with readable date format -->
        <template v-slot:item.dateEnd="{ item }">
          {{ item.dateEnd | readableDateTime }}
        </template>

        <!-- Registration status column -->
        <template v-slot:item.registrationStatus="{ item }">
          <v-chip
            :color="getRegistrationStatusColor(item)"
            :text-color="getRegistrationStatusTextColor(item)"
            small
          >
            {{ getRegistrationStatusText(item) }}
          </v-chip>
        </template>

        <!-- Actions column -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex flex-column flex-sm-row gap-2">
            <v-btn
              color="primary"
              outlined
              small
              class="mb-1 mb-sm-0 mr-sm-2"
              @click="openSessionDetailsDialog(item)"
            >
              <v-icon left small>mdi-account-group</v-icon>
              View Users
            </v-btn>
            <v-btn
              color="success"
              outlined
              small
              @click="openSessionCountsDialog(item)"
            >
              <v-icon left small>mdi-monitor-dashboard</v-icon>
              Live Display
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Loading indicator -->
      <v-progress-linear v-if="sessionsLoading" class="mt-4" indeterminate></v-progress-linear>
    </v-card>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { format } from 'date-fns';
import SessionDetailsDialog from '@/components/Sessions/SessionDetailsDialog';
import SessionCountsDisplayDialog from '@/components/Sessions/SessionCountsDisplayDialog';

export default {
  name: 'Sessions',
  components: {
    SessionDetailsDialog,
    SessionCountsDisplayDialog,
  },
  filters: {
    /**
     * Converts a date string to a human-readable date time format.
     * @param {string} value - The date string.
     * @returns {string} Formatted date and time.
     */
    readableDateTime: (value) => {
      if (!value) return '';
      return format(new Date(value), 'MMM d, y h:mm a');
    },
  },
  data() {
    return {
      tableSearch: '',
      sessionDetailsDialog: {
        visible: false,
        session: null,
      },
      sessionCountsDialog: {
        visible: false,
        session: null,
      },
      tableHeaders: [
        {
          text: 'Session Name',
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Description',
          value: 'description',
          sortable: true,
        },
        {
          text: 'Start Date/Time',
          value: 'dateStart',
          sortable: true,
        },
        {
          text: 'End Date/Time',
          value: 'dateEnd',
          sortable: true,
        },
        {
          text: 'Limit',
          value: 'limit',
          sortable: true,
          align: 'center',
        },
        {
          text: 'Registered',
          value: 'registrantsCount',
          sortable: true,
          align: 'center',
        },
        {
          text: 'Attended',
          value: 'attendantsCount',
          sortable: true,
          align: 'center',
        },
        {
          text: 'Status',
          value: 'registrationStatus',
          sortable: false,
          align: 'center',
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
        },
      ],
    };
  },
  computed: {
    ...mapState(['sessions', 'sessionsLoading']),

    /**
     * Filters sessions based on search input
     * @returns {Array} Filtered sessions array
     */
    filteredSessions() {
      if (!this.tableSearch) {
        return this.sessions;
      }

      const search = this.tableSearch.toLowerCase();
      return this.sessions.filter((session) => (
        (session.name && session.name.toLowerCase().includes(search))
        || (session.description && session.description.toLowerCase().includes(search))
      ));
    },
  },
  async created() {
    await this.getSessionsList();
  },
  methods: {
    ...mapActions(['getSessionsAction']),

    /**
     * Fetches the sessions list from the store
     */
    async getSessionsList() {
      await this.getSessionsAction();
    },

    /**
     * Opens the session details dialog with user list
     * @param {Object} session - The session object
     */
    openSessionDetailsDialog(session) {
      this.sessionDetailsDialog = {
        visible: true,
        session,
      };
    },

    /**
     * Closes the session details dialog
     */
    closeSessionDetailsDialog() {
      this.sessionDetailsDialog.visible = false;
    },

    /**
     * Opens the session counts display dialog
     * @param {Object} session - The session object
     */
    openSessionCountsDialog(session) {
      this.sessionCountsDialog = {
        visible: true,
        session,
      };
    },

    /**
     * Closes the session counts display dialog
     */
    closeSessionCountsDialog() {
      this.sessionCountsDialog.visible = false;
    },

    /**
     * Gets the registration status color based on capacity
     * @param {Object} session - The session object
     * @returns {string} Color name
     */
    getRegistrationStatusColor(session) {
      const percentage = (session.registrantsCount / session.limit) * 100;
      if (percentage >= 100) return 'error';
      if (percentage >= 80) return 'warning';
      return 'success';
    },

    /**
     * Gets the registration status text color
     * @param {Object} session - The session object
     * @returns {string} Text color
     */
    getRegistrationStatusTextColor(session) {
      const percentage = (session.registrantsCount / session.limit) * 100;
      if (percentage >= 80) return 'white';
      return 'black';
    },

    /**
     * Gets the registration status text
     * @param {Object} session - The session object
     * @returns {string} Status text
     */
    getRegistrationStatusText(session) {
      const percentage = (session.registrantsCount / session.limit) * 100;
      if (percentage >= 100) return 'Full';
      if (percentage >= 80) return 'Nearly Full';
      return 'Available';
    },
  },
};
</script>
